import * as React from 'react'

import { Box, Button, Href, NumberBadge, Tag, Text } from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'

import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  card_reward,
  isCountryAvailable,
  referralUrlForLocale,
  termsAndConditionsUrl,
} from '../helpers'
import { RewardCard } from '../reward'

type Props = {
  title: string
  iconNumber?: number
  description?: string
  children?: React.ReactNode
}

const InfoItem = ({ title, iconNumber, description, children }: Props) => {
  return (
    <Box flexDirection="row" gap="s6">
      {iconNumber && <NumberBadge label={iconNumber} />}
      <Box gap="s4">
        <Text.Normal weight="bold">{title}</Text.Normal>
        {description && <Text.Normal>{description}</Text.Normal>}
        {children}
      </Box>
    </Box>
  )
}

export const Content = () => {
  const {
    access: { userId, locale },
    company,
    employee,
  } = useCurrentSession()
  const mainLegalEntity = company.legalEntities.find((e) => e.main)
  const isoCountryCode = mainLegalEntity?.country || employee?.legalEntity?.country
  const base64Code = btoa(JSON.stringify({ companyId: company.id, userId }))
  const shareUrl = `${referralUrlForLocale(locale.toLowerCase())}/${base64Code}`

  useUnsafeEffect(() => {
    track(trackingEvents.settings.visitReferralProgramPage)
  }, [])

  return (
    <Box maxWidth="narrowPage" paddingX="s32" alignSelf="center">
      <Box flexDirection="row" paddingBottom="s24">
        <Tag type="radical">
          {i18n.t('company_settings.general.factorial_referral_program.title_tag')}
        </Tag>
      </Box>
      <Box paddingBottom="s24" gap="s8">
        <Text color="grey1000" weight="bold" size={'500'}>
          {i18n.t('company_settings.general.factorial_referral_program.title')}
        </Text>
      </Box>

      {isoCountryCode && isCountryAvailable(isoCountryCode) ? (
        <Box gap="s24">
          <Text.Normal>
            {i18n.t('company_settings.general.factorial_referral_program.content_1')}
          </Text.Normal>
          <Box gap="s16" width="fit-content">
            {[1, 2, 3, 4].map((step) => (
              <RewardCard key={step} step={step.toString()} isoCountryCode={isoCountryCode} />
            ))}
          </Box>
          <Box gap="s12">
            <Text.Normal>
              {i18n.t('company_settings.general.factorial_referral_program.content_2')}
            </Text.Normal>
            <Text.Normal>
              {i18n.t('company_settings.general.factorial_referral_program.content_3')}
            </Text.Normal>
          </Box>
          <Box>
            <Text.Normal>
              {i18n.t('company_settings.general.factorial_referral_program.copy_and_share')}
            </Text.Normal>
            <TextField
              readonly
              isClickableIcon
              value={shareUrl}
              indicator={
                <Button
                  type="secondary"
                  onClick={() => {
                    copy(shareUrl)
                    app.setFlash({
                      type: 'correct',
                      message: i18n.t('common.copied'),
                    })
                    track(trackingEvents.settings.copyReferralProgramLink)
                  }}
                  iconName="paperclip"
                />
              }
            />
          </Box>
          <Box gap="s12">
            <Text color="grey1000" weight="bold" size={'400'}>
              {i18n.t('company_settings.general.factorial_referral_program.reward_title', {
                reward: card_reward(isoCountryCode),
              })}
            </Text>
            <InfoItem
              iconNumber={1}
              title={i18n.t('company_settings.general.factorial_referral_program.title_3')}
            />
            <InfoItem
              iconNumber={2}
              title={i18n.t('company_settings.general.factorial_referral_program.title_4')}
              description={i18n.t('company_settings.general.factorial_referral_program.content_4')}
            />
            <InfoItem
              iconNumber={2}
              title={i18n.t('company_settings.general.factorial_referral_program.title_5')}
              description={i18n.t(
                'company_settings.general.factorial_referral_program.reward_content',
                {
                  reward: card_reward(isoCountryCode),
                }
              )}
            />
            <Text.Normal>
              {i18n.t('company_settings.general.factorial_referral_program.content_3')}
            </Text.Normal>
          </Box>

          <Box flexDirection="row" gap="s4" alignItems="baseline">
            <Text.Normal>
              {i18n.t('company_settings.general.factorial_referral_program.more_info')}
            </Text.Normal>
            <Href href={termsAndConditionsUrl(isoCountryCode)} target="_blank">
              <Text.Normal size="200" color="viridian1200" underlined>
                {i18n.t('company_settings.general.factorial_referral_program.text_link')}
              </Text.Normal>
            </Href>
          </Box>
          <Text.Normal size="200">
            {i18n.t('company_settings.general.factorial_referral_program.card_disclaimer')}
          </Text.Normal>
        </Box>
      ) : (
        <Text.Normal>
          {i18n.t('company_settings.general.factorial_referral_program.soon')}
        </Text.Normal>
      )}
    </Box>
  )
}
